import { ConnectMetaButton } from './ConnectMetaButton';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, ShieldOff, Clock, ExternalLink, Link as LinkIcon } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '@/components/Button';
import { MetaAdAccount } from '@/services/facebook-ad-account.service';

interface Props {
  account: MetaAdAccount | null;
}

/**
 * Meta accountStatus enum (Graph API):
 *   1   ACTIVE
 *   2   DISABLED
 *   3   UNSETTLED
 *   7   PENDING_RISK_REVIEW
 *   8   PENDING_SETTLEMENT
 *   9   IN_GRACE_PERIOD
 *   100 PENDING_CLOSURE
 *   101 CLOSED
 */
const STATUS_LABELS: Record<number, string> = {
  1: 'Active',
  2: 'Disabled',
  3: 'Unsettled',
  7: 'Pending risk review',
  8: 'Pending settlement',
  9: 'In grace period',
  100: 'Pending closure',
  101: 'Closed',
};

interface BannerKind {
  tone: 'danger' | 'warning' | 'info';
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  reconnect?: boolean;
  cta?: { label: string; onClick: () => void };
}

function pickBanner(account: MetaAdAccount | null, goToAdAccount?: () => void): BannerKind | null {
  if (!account) return null;

  // 1) Token issues take top priority — without a working token nothing else works.
  if (account.tokenRefreshError) {
    return {
      tone: 'danger',
      icon: <ShieldOff size={16} />,
      title: 'Meta connection broken',
      body: (
        <>
          We tried to refresh your Meta access token but it failed:{' '}
          <strong className="text-text-primary">{account.tokenRefreshError}</strong>. Reconnect with Meta to continue managing campaigns.
        </>
      ),
      reconnect: true,
    };
  }

  if (account.tokenExpiresAt) {
    const expiresAt = new Date(account.tokenExpiresAt);
    const daysLeft = Math.floor((expiresAt.getTime() - Date.now()) / 86_400_000);
    if (daysLeft < 0) {
      return {
        tone: 'danger',
        icon: <ShieldOff size={16} />,
        title: 'Meta token expired',
        body: 'Your Meta access token has expired. Reconnect with Meta to keep your campaigns syncing and editable.',
        reconnect: true,
      };
    }
    if (daysLeft <= 7) {
      return {
        tone: 'warning',
        icon: <Clock size={16} />,
        title: `Meta token expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`,
        body: 'We auto-refresh tokens, but if the auto-refresh fails you\'ll lose access. Reconnect now to be safe.',
        reconnect: true,
      };
    }
  }

  // 2) No Facebook Page connected — ads can't be created without one. We surface
  // this BEFORE the user invests time in a campaign so they don't run into a
  // PAGE_NOT_CONNECTED 422 at the very last save step.
  const authorizedPages = account.pageIds && account.pageIds.length > 0
    ? account.pageIds
    : account.pageId ? [account.pageId] : [];
  if (authorizedPages.length === 0) {
    return {
      tone: 'warning',
      icon: <LinkIcon size={16} />,
      title: 'Connect a Facebook Page first',
      body: (
        <>
          Ads run as a Facebook Page. Pick at least one Page on the{' '}
          <strong className="text-text-primary">Ad Account Settings</strong> page before creating a
          campaign — otherwise the final save step will fail.
        </>
      ),
      cta: goToAdAccount ? { label: 'Connect a Page', onClick: goToAdAccount } : undefined,
    };
  }

  // 3) Account-level health — disabled / grace / risk review etc.
  if (account.accountStatus !== undefined && account.accountStatus !== 1) {
    const label = STATUS_LABELS[account.accountStatus] ?? `Status ${account.accountStatus}`;
    const isFatal = account.accountStatus === 2 || account.accountStatus === 101 || account.accountStatus === 100;
    const isGrace = account.accountStatus === 9 || account.accountStatus === 7 || account.accountStatus === 8;
    return {
      tone: isFatal ? 'danger' : isGrace ? 'warning' : 'info',
      icon: <AlertTriangle size={16} />,
      title: `Ad account: ${label}`,
      body: (
        <>
          Meta has flagged this ad account ({account.adAccountId}). Ad creation and edits may be blocked.{' '}
          <a
            href="https://business.facebook.com/billing_hub/payment_settings"
            target="_blank"
            rel="noreferrer"
            className="underline hover:no-underline"
          >
            Resolve in Business Manager <ExternalLink size={11} className="inline ml-0.5" />
          </a>
        </>
      ),
    };
  }

  return null;
}

export const AccountHealthBanner: React.FC<Props> = ({ account }) => {
  const navigate = useNavigate();
  const goToAdAccount = () => navigate('/facebook/ad-account');
  const banner = pickBanner(account, goToAdAccount);
  if (!banner) return null;

  const toneClasses = {
    danger:  'border-danger/40 bg-danger-soft text-danger',
    warning: 'border-warning/40 bg-warning-soft text-warning',
    info:    'border-info/40 bg-info-soft text-info',
  } as const;

  return (
    <div className={clsx('flex items-start gap-3 rounded-xl border px-4 py-3', toneClasses[banner.tone])}>
      <span className="mt-0.5 shrink-0">{banner.icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-text-primary">{banner.title}</div>
        <div className="mt-0.5 text-xs text-text-secondary leading-relaxed">{banner.body}</div>
      </div>
      {banner.reconnect && <ConnectMetaButton label="Reconnect with Meta" compact onConnected={goToAdAccount} />}
      {banner.cta && (
        <Button size="sm" onClick={banner.cta.onClick} iconLeft={<RefreshCw size={12} />}>
          {banner.cta.label}
        </Button>
      )}
    </div>
  );
};
